import React, { useEffect, useMemo, useState } from 'react';
import './DashboardPage.css';
import {
  getAnalyticsLogs,
  getAnalyticsOrderRate,
  getAnalyticsOverview,
  getAnalyticsSites,
  getSites,
} from '../api';

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'trends', label: 'Order Trends' },
  { id: 'sites', label: 'Site Performance' },
  { id: 'subs', label: 'Substitutions' },
  { id: 'logs', label: 'Logs' },
];

function buildPeriodParams(period, customFrom, customTo, siteId) {
  const params = { period };
  if (period === 'custom') {
    if (customFrom) params.from = customFrom;
    if (customTo) params.to = customTo;
  }
  if (siteId) params.siteId = siteId;
  return params;
}

export default function DashboardPage() {
  const [activeTab, setActiveTab] = useState('overview');
  const [period, setPeriod] = useState('last_week');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [siteId, setSiteId] = useState('');

  const [sites, setSites] = useState([]);

  const [overview, setOverview] = useState(null);
  const [siteRows, setSiteRows] = useState([]);
  const [rate, setRate] = useState(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);

  const [logs, setLogs] = useState({ rows: [], total: 0, page: 1, pageSize: 25 });
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [logQuery, setLogQuery] = useState('');
  const [logAction, setLogAction] = useState('');
  const [logEntityType, setLogEntityType] = useState('');
  const [logUserEmail, setLogUserEmail] = useState('');
  const [logPage, setLogPage] = useState(1);
  const [logPageSize, setLogPageSize] = useState(25);

  const [errorMsg, setErrorMsg] = useState('');

  const periodParams = useMemo(
    () => buildPeriodParams(period, customFrom, customTo, siteId),
    [period, customFrom, customTo, siteId]
  );

  useEffect(() => {
    getSites()
      .then((res) => setSites(res.data || []))
      .catch(() => setSites([]));
  }, []);

  useEffect(() => {
    const loadAnalytics = async () => {
      setLoadingAnalytics(true);
      setErrorMsg('');
      try {
        const [overviewRes, sitesRes, rateRes] = await Promise.all([
          getAnalyticsOverview(periodParams),
          getAnalyticsSites(periodParams),
          getAnalyticsOrderRate(periodParams),
        ]);
        setOverview(overviewRes.data || null);
        setSiteRows(sitesRes.data?.rows || []);
        setRate(rateRes.data || null);
      } catch (e) {
        setErrorMsg(e.response?.data?.error || 'Failed to load analytics');
      } finally {
        setLoadingAnalytics(false);
      }
    };

    if (period !== 'custom' || (customFrom && customTo)) {
      loadAnalytics();
    }
  }, [periodParams, period, customFrom, customTo]);

  useEffect(() => {
    if (activeTab !== 'logs') return;

    const loadLogs = async () => {
      setLoadingLogs(true);
      setErrorMsg('');
      try {
        const params = {
          page: logPage,
          pageSize: logPageSize,
          query: logQuery || undefined,
          action: logAction || undefined,
          entityType: logEntityType || undefined,
          userEmail: logUserEmail || undefined,
          siteId: siteId || undefined,
        };
        if (period === 'custom') {
          if (customFrom) params.from = customFrom;
          if (customTo) params.to = customTo;
        }
        const res = await getAnalyticsLogs(params);
        setLogs(res.data || { rows: [], total: 0, page: logPage, pageSize: logPageSize });
      } catch (e) {
        setErrorMsg(e.response?.data?.error || 'Failed to load logs');
      } finally {
        setLoadingLogs(false);
      }
    };

    loadLogs();
  }, [activeTab, period, customFrom, customTo, siteId, logPage, logPageSize, logQuery, logAction, logEntityType, logUserEmail]);

  const totals = overview?.totals || {
    ordersSubmitted: 0,
    orderValue: 0,
    packsSubmitted: 0,
    productsSubmitted: 0,
    substitutions: 0,
  };

  const maxTrend = Math.max(...(rate?.series || []).map(s => s.ordersRaised || 0), 1);
  const actions = [...new Set((logs.rows || []).map(r => r.Action).filter(Boolean))].sort();

  return (
    <>
      <div className="top-header">
        <h1>Performance & Analytics</h1>
        <div className="top-header-actions">
          <span className="text-muted text-sm">Operational insight across orders, sites, substitutions, and activity logs</span>
        </div>
      </div>

      <div className="page-content">
        <div className="hs-card mb-lg">
          <div className="hs-card-header">
            <h2>Filters</h2>
          </div>
          <div className="analytics-card-body">
            <div className="analytics-filter-grid">
            <div>
              <label className="text-sm text-muted">Period</label>
              <select className="hs-input" value={period} onChange={(e) => setPeriod(e.target.value)}>
                <option value="last_week">Last Week</option>
                <option value="last_month">Last Month</option>
                <option value="this_year">This Year</option>
                <option value="custom">Custom</option>
              </select>
            </div>

            {period === 'custom' && (
              <>
                <div>
                  <label className="text-sm text-muted">From</label>
                  <input className="hs-input" type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} />
                </div>
                <div>
                  <label className="text-sm text-muted">To</label>
                  <input className="hs-input" type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} />
                </div>
              </>
            )}

            <div>
              <label className="text-sm text-muted">Franchise Site</label>
              <select className="hs-input" value={siteId} onChange={(e) => setSiteId(e.target.value)}>
                <option value="">All Sites</option>
                {sites.map((site) => (
                  <option key={site.SiteId} value={site.SiteId}>{site.SiteCode} - {site.SiteName}</option>
                ))}
              </select>
            </div>
            </div>
            {period === 'custom' && (!customFrom || !customTo) && (
              <div className="text-muted text-sm mt-sm">Pick both custom dates to load analytics.</div>
            )}
            {errorMsg && <div className="text-danger mt-sm">{errorMsg}</div>}
          </div>
        </div>

        <div className="hs-card mb-lg">
          <div className="analytics-card-body analytics-tab-strip">
            {TABS.map(tab => (
              <button
                key={tab.id}
                type="button"
                className={`hs-btn hs-btn-sm ${activeTab === tab.id ? 'hs-btn-primary' : 'hs-btn-secondary'}`}
                onClick={() => {
                  setActiveTab(tab.id);
                  if (tab.id === 'logs') setLogPage(1);
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {loadingAnalytics && activeTab !== 'logs' && (
          <div className="hs-card">Loading analytics...</div>
        )}

        {activeTab === 'overview' && !loadingAnalytics && (
          <>
            <div className="analytics-kpi-grid">
              <div className="stat-card"><div className="stat-value">{totals.ordersSubmitted}</div><div className="stat-label">Submitted Orders</div></div>
              <div className="stat-card"><div className="stat-value">£{Number(totals.orderValue || 0).toFixed(2)}</div><div className="stat-label">Submitted Value</div></div>
              <div className="stat-card"><div className="stat-value">{totals.packsSubmitted}</div><div className="stat-label">Packs Submitted</div></div>
              <div className="stat-card"><div className="stat-value">{totals.productsSubmitted}</div><div className="stat-label">Products Submitted</div></div>
              <div className="stat-card"><div className="stat-value">{totals.substitutions}</div><div className="stat-label">Substitutions</div></div>
            </div>

            <div className="hs-card">
              <div className="hs-card-header"><h2>Top Substitution Reasons</h2></div>
              <div className="analytics-table-wrap">
                <table className="hs-table analytics-table">
                  <thead>
                    <tr><th>Reason</th><th className="text-right">Count</th></tr>
                  </thead>
                  <tbody>
                    {(overview?.substitutionsByReason || []).length === 0 ? (
                      <tr><td colSpan="2" className="text-center text-muted" style={{ padding: '24px' }}>No substitutions in selected period.</td></tr>
                    ) : (
                      (overview?.substitutionsByReason || []).map((row, idx) => (
                        <tr key={`${row.Reason}-${idx}`}>
                          <td>{row.Reason}</td>
                          <td className="text-right"><strong>{row.Total}</strong></td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {activeTab === 'trends' && !loadingAnalytics && (
          <div className="hs-card">
            <div className="hs-card-header">
              <h2>Order Raising Rate</h2>
            </div>
            <div className="analytics-card-body">

              <div className="analytics-summary-grid">
                <div className="analytics-summary-item"><span className="text-muted text-sm">Total</span><strong>{rate?.summary?.total || 0}</strong></div>
                <div className="analytics-summary-item"><span className="text-muted text-sm">Avg / Day</span><strong>{rate?.summary?.avgPerDay || 0}</strong></div>
                <div className="analytics-summary-item"><span className="text-muted text-sm">Previous Window</span><strong>{rate?.summary?.previousTotal || 0}</strong></div>
                <div className="analytics-summary-item">
                  <span className="text-muted text-sm">Delta</span>
                  <strong style={{ color: (rate?.summary?.delta || 0) >= 0 ? '#16a34a' : '#dc2626' }}>
                    {(rate?.summary?.delta || 0) >= 0 ? '+' : ''}{rate?.summary?.delta || 0}
                    {typeof rate?.summary?.deltaPct === 'number' ? ` (${rate.summary.deltaPct >= 0 ? '+' : ''}${rate.summary.deltaPct}%)` : ''}
                  </strong>
                </div>
              </div>

              <div className="analytics-trend-list">
                {(rate?.series || []).map((point) => (
                  <div key={point.date} className="analytics-trend-row">
                    <span className="font-mono text-sm">{point.date}</span>
                    <div className="analytics-trend-track">
                      <div
                        className="analytics-trend-fill"
                        style={{ width: `${Math.max(2, ((point.ordersRaised || 0) / maxTrend) * 100)}%` }}
                      />
                    </div>
                    <strong className="text-right">{point.ordersRaised || 0}</strong>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'sites' && !loadingAnalytics && (
          <div className="hs-card">
            <div className="hs-card-header"><h2>Orders and Value by Franchise Site</h2></div>
            <div className="analytics-table-wrap">
              <table className="hs-table analytics-table analytics-site-table">
                <colgroup>
                  <col style={{ width: '38%' }} />
                  <col style={{ width: '12%' }} />
                  <col style={{ width: '18%' }} />
                  <col style={{ width: '10%' }} />
                  <col style={{ width: '12%' }} />
                  <col style={{ width: '10%' }} />
                </colgroup>
                <thead>
                  <tr>
                    <th>Site</th>
                    <th className="text-right analytics-col-num">Orders</th>
                    <th className="text-right analytics-col-num analytics-col-value">Value</th>
                    <th className="text-right analytics-col-num">Packs</th>
                    <th className="text-right analytics-col-num">Products</th>
                    <th className="text-right analytics-col-num">Subs</th>
                  </tr>
                </thead>
                <tbody>
                  {siteRows.length === 0 ? (
                    <tr><td colSpan="6" className="text-center text-muted" style={{ padding: '24px' }}>No data for selected period.</td></tr>
                  ) : (
                    siteRows.map((row) => (
                      <tr key={row.SiteId}>
                        <td><strong>{row.SiteCode}</strong> - {row.SiteName}</td>
                        <td className="text-right analytics-col-num">{row.OrdersSubmitted}</td>
                        <td className="text-right font-mono analytics-col-num analytics-col-value">£{Number(row.SubmittedValue || 0).toFixed(2)}</td>
                        <td className="text-right analytics-col-num">{row.PacksSubmitted}</td>
                        <td className="text-right analytics-col-num">{row.ProductsSubmitted}</td>
                        <td className="text-right analytics-col-num">{row.Substitutions}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'subs' && !loadingAnalytics && (
          <div className="hs-card">
            <div className="hs-card-header"><h2>Substitution Reason Breakdown</h2></div>
            <div className="analytics-table-wrap">
              <table className="hs-table analytics-table">
                <thead>
                  <tr><th>Reason</th><th className="text-right">Count</th></tr>
                </thead>
                <tbody>
                  {(overview?.substitutionsByReason || []).length === 0 ? (
                    <tr><td colSpan="2" className="text-center text-muted" style={{ padding: '24px' }}>No substitution events in this period.</td></tr>
                  ) : (
                    (overview?.substitutionsByReason || []).map((row, idx) => (
                      <tr key={`${row.Reason}-${idx}`}>
                        <td>{row.Reason}</td>
                        <td className="text-right"><strong>{row.Total}</strong></td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'logs' && (
          <div className="hs-card">
            <div className="hs-card-header"><h2>Application Logs</h2></div>
            <div className="analytics-card-body">

              <div className="analytics-log-filter-grid">
                <input className="hs-input" placeholder="Search action, email, payload" value={logQuery} onChange={(e) => { setLogQuery(e.target.value); setLogPage(1); }} />
                <select className="hs-input" value={logAction} onChange={(e) => { setLogAction(e.target.value); setLogPage(1); }}>
                  <option value="">All Actions</option>
                  {actions.map((a) => <option key={a} value={a}>{a}</option>)}
                </select>
                <select className="hs-input" value={logEntityType} onChange={(e) => { setLogEntityType(e.target.value); setLogPage(1); }}>
                  <option value="">All Entity Types</option>
                  <option value="Order">Order</option>
                  <option value="OrderLine">OrderLine</option>
                  <option value="User">User</option>
                </select>
                <input className="hs-input" placeholder="User email" value={logUserEmail} onChange={(e) => { setLogUserEmail(e.target.value); setLogPage(1); }} />
              </div>

              {loadingLogs ? (
                <div className="text-muted">Loading logs...</div>
              ) : (
                <>
                  <div className="analytics-table-wrap">
                    <table className="hs-table analytics-table analytics-logs-table">
                      <thead>
                        <tr>
                          <th>Time</th>
                          <th>Action</th>
                          <th>User</th>
                          <th>Entity</th>
                          <th>Order</th>
                          <th>Site</th>
                          <th>Notes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(logs.rows || []).length === 0 ? (
                          <tr><td colSpan="7" className="text-center text-muted" style={{ padding: '24px' }}>No logs match current filters.</td></tr>
                        ) : (
                          (logs.rows || []).map((row) => (
                            <tr key={row.LogId}>
                              <td className="font-mono text-sm">{new Date(row.CreatedAt).toLocaleString()}</td>
                              <td><span className="hs-badge hs-badge-draft">{row.Action}</span></td>
                              <td>{row.UserEmail || 'System'}</td>
                              <td>{row.EntityType}#{row.EntityId || '—'}</td>
                              <td>{row.OrderNumber || '—'}</td>
                              <td>{row.SiteCode || '—'}</td>
                              <td>{row.Notes || '—'}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>

                  <div className="analytics-pagination-row">
                    <div className="text-muted text-sm">Total logs: {logs.total || 0}</div>
                    <div className="analytics-pagination-controls">
                      <select className="hs-input" value={logPageSize} onChange={(e) => { setLogPageSize(parseInt(e.target.value, 10)); setLogPage(1); }}>
                        <option value={25}>25 / page</option>
                        <option value={50}>50 / page</option>
                        <option value={100}>100 / page</option>
                      </select>
                      <button className="hs-btn hs-btn-secondary hs-btn-sm" onClick={() => setLogPage((p) => Math.max(1, p - 1))} disabled={logPage <= 1}>Prev</button>
                      <span className="text-sm">Page {logs.page || logPage}</span>
                      <button
                        className="hs-btn hs-btn-secondary hs-btn-sm"
                        onClick={() => setLogPage((p) => p + 1)}
                        disabled={(logs.page || 1) * (logs.pageSize || logPageSize) >= (logs.total || 0)}
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
}
